import {createRouter, createWebHistory} from 'vue-router'
import {type IStaticMethods} from "preline/preline";
import {allRoutes} from "@/router/routes";
import {appTitle} from "@/helpers";
import {useAuthStore} from '@/stores/auth';
import {useUserStore} from "@/stores/user";
import {useLegalStore} from "@/stores/legal";
import {useProfileStore} from '@/stores/profile';

declare global {
    interface Window {
        HSStaticMethods: IStaticMethods;
    }
}


const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes: allRoutes,
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition) {
            return savedPosition;
        }

        if (to.hash) {
            return {
                el: to.hash,
                behavior: 'smooth',
            };
        }

        return {left: 0, top: 0};
    }
})


// set page title
router.beforeEach((to, from, next) => {
    const title = to.meta.title;
    if (title) {
        document.title = title + ' | ' + appTitle;
    } else {
        document.title = appTitle;
    }

    next();
})

// preline
router.afterEach((to, from, failure) => {
    if (!failure) {
        setTimeout(() => {
            window.HSStaticMethods.autoInit();
        }, 100)
    }
});


// auth
router.beforeEach(async (to, from, next) => {

    const useAuth = useAuthStore()
    const useUser = useUserStore()
    const useLegal = useLegalStore()
    const useProfile = useProfileStore()

    // If the user is not authenticated, redirect to login / this is intentionally after user fetch
    // because in fetchCurrentUser authenticated is set to true.
    // alternatively we could create auth.init() function starting in main.ts
    const requiresAuth = to.matched.some((route) => route.meta.requiresAuth)

    function redirectToLogin() {
        // if authentication is required, redirect to login page
        return next({name: 'auth.sign-in', query: {redirectedFrom: to.fullPath}})
    }
    // Ensure the user is loaded before navigating
    if (!useUser.myUser) {
        // if no token in store the user is not fetched and just return null.
        try {
            await useAuth.fetchCurrentUser();
        } catch (e) {
            log.error("User fetch failed", e);
            //return redirectToLogin()
        }
    }
    // Fetch profile only if authenticated and not already fetched
    if (useAuth.isAuthenticated && !useProfile.myProfile) {
        try {
            await useProfile.fetchMyProfile();
        } catch (e) {
            log.warn("Profile fetch failed", e); // or handle more gracefully
            return redirectToLogin()
        }
    }
    // Fetch profile only if authenticated and not already fetched
    if (useAuth.isAuthenticated && !useLegal.currentLegal) {
        try {
            await useLegal.fetchCurrentLegal();
        } catch (e) {
            log.warn("Legal fetch failed", e); // or handle more gracefully
            return redirectToLogin()
        }
    }



    // if no authentication is required, just continue
    // Enforce legal acceptance for authenticated users until they accept terms.
    const needsLegalAcceptance = useAuth.isAuthenticated && !!useLegal.currentLegal?.status?.needsAcceptance;
    const isLegalRoute = to.name === "my.legal";
    if (needsLegalAcceptance && !isLegalRoute) {
        return next({name: "my.legal", query: {redirectedFrom: to.fullPath}});
    }

    if (!requiresAuth) return next()

    // Re-check auth status after fetch
    if (!useAuth.isAuthenticated) {
        return redirectToLogin()
    }

    return next()
})

export default router
